import {
  Avatar,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { getMobileTableStyles } from "../../components/MobileTable";
import { User } from "../../utils";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { SUBSCRIPTION_TYPES } from "../../constants/subscriptionTypes";
import { useWhitelabel } from "../../context/WhitelabelContext";
import { Company } from "../../types";
import {
  getSupportContactInfo,
  getUserScopedKey,
  parseError,
  toAvatarName,
} from "../../utils";
import { isFreeUser } from "../../utils/subscriptionUtils";
import ChatApi from "../chat/chatApi";

import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { hostname } from "../../utils";
import CompanyCreateModal from "./CompanyCreateModal";
import TransferOwnershipDialog from "./dialogs/TransferOwnershipDialog";
import { SELECTABLE_FEATURES } from "../../constants/features";

const Companies = ({
  currentUser,
  token,
  getToken,
  eventListener,
  openDialog,
  onNewOrganization,
}: {
  currentUser: any | User;
  token: any | string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  openDialog: (title: string, content: string, buttonText: string, onClick: () => void) => void;
  onNewOrganization: (company: Company) => void;
}) => {
  const [companies, setCompanies] = useState<Array<Company>>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuCompany, setMenuCompany] = useState<Company | null>(null);
  const [usage, setUsage] = useState<any>({});
  const [usageLoaded, setUsageLoaded] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(SUBSCRIPTION_TYPES.FREE);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedCompanyForTransfer, setSelectedCompanyForTransfer] = useState<Company | null>(null);
  const navigate = useNavigate();
  const { whitelabelSettings } = useWhitelabel();
  const theme = useTheme();

  const chatApi = new ChatApi();
  chatApi.init(eventListener, getToken);

  // Get user subscription and usage data
  const getUsage = async () => {
    try {
      const currentToken = await getToken();
      const response = await axios.get(`${hostname}/usage`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      setUsage(response.data.usage);
      setUsageLoaded(true);
      if (response.data.usage.subscriptionType) {
        setCurrentPlan(response.data.usage.subscriptionType);
      }
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  };

  // Calculate company creation limits based on database field
  const getCompanyLimits = () => {
    // Use companyLimit field from database, fallback to 1 if not set (consistent with Profile page)
    const maxCompanies = usage.companyLimit || 1;
    const currentSeats = usage.seatCount || usage.seats || 1;
    // Use companiesUsed from backend which counts only owned companies
    const companiesUsed = usage.companiesUsed || 0;

    const isOrgManaged = !!usage.isOrgManaged;
    // Super users bypass org-managed and limit restrictions so they can always
    // create a company (e.g. while working inside an org for support/testing).
    const isSuperUser = !!usage.superUser;
    return {
      maxCompanies,
      currentSeats,
      companiesUsed,
      canCreate: isSuperUser ? true : isOrgManaged ? false : companiesUsed < maxCompanies,
      upgradeRequired: isSuperUser ? false : isOrgManaged ? false : companiesUsed >= maxCompanies,
      isOrgManaged,
      isSuperUser,
    };
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    company: Company
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuCompany(company);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuCompany(null);
  };

  const deleteCompany = async (id: string, name: string) => {
    handleMenuClose();
    const currentToken = await getToken();
    openDialog(
      "Delete Company",
      `Are you sure you want to delete company ${name}?`,
      "Confirm",
      () => {
        setLoading(true);
        axios
          .delete(`${hostname}/companies/${id}`, {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          })
          .catch((err) => {
            eventListener({
              type: "SET_ERROR",
              error: parseError(err),
            });
          })
          .finally(() => {
            getCompanies();
          });
      }
    );
  };

  const handleTransferOwnership = (company: Company) => {
    handleMenuClose();
    setSelectedCompanyForTransfer(company);
    setTransferDialogOpen(true);
  };

  const leaveCompany = async (id: string, name: string) => {
    handleMenuClose();
    openDialog(
      "Leave Company",
      `Are you sure you want to leave ${name}? You will lose access to this company.`,
      "Leave",
      async () => {
        setLoading(true);
        const currentToken = await getToken();
        axios
          .post(
            `${hostname}/companies/${id}/leave`,
            {},
            {
              headers: {
                Authorization: `Bearer ${currentToken}`,
              },
            }
          )
          .catch((err) => {
            eventListener({
              type: "SET_ERROR",
              error: parseError(err),
            });
          })
          .finally(() => {
            getCompanies();
          });
      }
    );
  };

  const getCompanies = async (showLoading: boolean = true) => {
    if (showLoading) {
      setLoading(true);
    }
    const currentToken = await getToken();
    await axios
      .get(`${hostname}/companies`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setCompanies(res.data.companies);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
    if (showLoading) {
      setLoading(false);
    }
  };

  const handleEditCompany = (company: Company) => {
    handleMenuClose();
    setSelectedCompany(company);
    setModalOpen(true);
  };

  const handleOpenCompany = (companyId: string) => {
    localStorage.setItem(getUserScopedKey(currentUser.uid), companyId);
    handleMenuClose();
    navigate(`/dashboard/companies/${companyId}`);
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(companies);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update local state immediately for better UX
    const reorderedCompanies = items.map((item, index) => ({
      ...item,
      displayOrder: index,
    }));
    setCompanies(reorderedCompanies);

    // Send update to backend
    try {
      const currentToken = await getToken();
      const companyOrders = reorderedCompanies.map((comp, index) => ({
        id: comp.id,
        displayOrder: index,
      }));

      await axios.post(
        `${hostname}/companies/order`,
        {
          companies: companyOrders,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
    } catch (err) {
      // Revert on error
      getCompanies();
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  };

  useEffect(() => {
    if (currentUser.uid) {
      getCompanies();
      getUsage();
    }
  }, [currentUser]);

  if (loading) {
    return <LinearProgress />;
  }

  const companyLimits = getCompanyLimits();

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, pt: 2 }}>
      <Typography variant="h4">
        Companies
        <Tooltip
          style={{ float: "right", marginTop: 10 }}
          title={`Virtual companies are used to train your virtual employees
                  about your business. The AI will be trained as a employee in this
                  virtual company and return answers relevant to the description and
                  industry. Each company will be self contained with no information
                  shared between them. This can be a shadow company of your existing
                  business or a business you want to start.
                  To get started, create a company, and start chatting with your
                  pre-generated employees.`}
        >
          <InfoOutlined />
        </Tooltip>
      </Typography>

      {/* Company limits info */}
      {!usageLoaded ? (
        <Box sx={{ mb: 4 }} />
      ) : !companyLimits.isOrgManaged || companyLimits.isSuperUser ? (
      <>
      {!companyLimits.isOrgManaged && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Companies: {companyLimits.companiesUsed} / {companyLimits.maxCompanies}
        </Typography>
      )}

      <Button
        data-tutorial="create-company"
        variant="contained"
        color="primary"
        style={{ marginBottom: 30, marginTop: 20 }}
        disabled={!companyLimits.canCreate}
        onClick={() => {
          if (companyLimits.upgradeRequired) {
            openDialog(
              "Company Limit Reached",
              `You've reached your company limit of ${
                companyLimits.maxCompanies
              }. ${
                isFreeUser(currentPlan)
                  ? "Upgrade to a paid plan to create more companies."
                  : "Contact support to increase your company limit or upgrade your plan."
              }`,
              isFreeUser(currentPlan) ? "Upgrade" : "Contact Support",
              () => {
                if (isFreeUser(currentPlan)) {
                  navigate("/");
                } else {
                  const supportUrl = getSupportContactInfo(whitelabelSettings);
                  if (supportUrl.startsWith("mailto:")) {
                    window.open(
                      `${supportUrl}?subject=Increase Company Limit`,
                      "_blank"
                    );
                  } else {
                    window.open(supportUrl, "_blank");
                  }
                }
              }
            );
          } else {
            // Send users through the full onboarding flow to create a company.
            // Pass fromCompanies so onboarding's "Exit" returns here instead of
            // logging the user out.
            navigate("/onboarding", { state: { fromCompanies: true } });
          }
        }}
      >
        <BusinessIcon style={{ marginRight: 5 }} />
        {companyLimits.upgradeRequired ? "Upgrade to Add More" : "Add Company"}
      </Button>
      </>
      ) : (
        <Box sx={{ mb: 4 }} />
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <TableContainer component={Paper} variant="outlined" data-tutorial="company-list" sx={getMobileTableStyles(2, 1)}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={30} sx={{ pl: 0.5, pr: 0 }}></TableCell>
                <TableCell width={60}></TableCell>
                <TableCell>
                  <strong>Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Industry</strong>
                </TableCell>
                <TableCell>
                  <Tooltip
                    title="The Billing Owner is the main owner responsible for subscription billing and company credits."
                    arrow
                    placement="top"
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                      <strong>Owner</strong>
                      <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <strong>Description</strong>
                </TableCell>
                <TableCell>
                  <strong>Quota</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <Droppable droppableId="companies">
              {(provided: any) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {companies.map((company, index) => (
                    <Draggable
                      key={company.id}
                      draggableId={company.id}
                      index={index}
                    >
                      {(provided: any, snapshot: any) => (
                        <TableRow
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          hover
                          style={{
                            ...provided.draggableProps.style,
                            cursor: snapshot.isDragging ? "grabbing" : "pointer",
                            backgroundColor: snapshot.isDragging
                              ? "rgba(0, 0, 0, 0.05)"
                              : "inherit",
                          }}
                          onClick={() => handleOpenCompany(company.id)}
                        >
                          <TableCell {...provided.dragHandleProps} sx={{ pl: 0.5, pr: 0 }}>
                            <DragIndicatorIcon
                              sx={{ cursor: "grab", color: "text.secondary", fontSize: 18 }}
                            />
                          </TableCell>
                          <TableCell sx={{ pl: 1 }}>
                            <Avatar src={company.logoUrl}>
                              {toAvatarName(company.name)}
                            </Avatar>
                          </TableCell>
                          <TableCell>{company.name}</TableCell>
                          <TableCell>{company.type}</TableCell>
                          <TableCell>
                            <Tooltip
                              title={company.ownerEmail || ""}
                              arrow
                              placement="top"
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Avatar
                                  src={company.ownerPhoto || undefined}
                                  sx={{ width: 24, height: 24, fontSize: 12 }}
                                >
                                  {company.ownerName
                                    ? toAvatarName(company.ownerName)
                                    : company.ownerEmail
                                    ? company.ownerEmail[0].toUpperCase()
                                    : "?"}
                                </Avatar>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                                  {company.ownerName || company.ownerEmail || "Unknown"}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            style={{
                              maxWidth: "400px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {company.description}
                          </TableCell>
                          <TableCell
                            onClick={(e) => e.stopPropagation()}
                            sx={{ minWidth: 150 }}
                          >
                            {company.creditLimit ? (
                              <Tooltip
                                title={`${company.creditUsage || 0} / ${company.creditLimit} credits used`}
                                arrow
                                placement="top"
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    width: "100%",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: theme.palette.text.primary,
                                      fontSize: "0.75rem",
                                      mb: 0.5,
                                    }}
                                  >
                                    {company.creditLimit > 0
                                      ? Math.round(
                                          ((company.creditUsage || 0) /
                                            company.creditLimit) *
                                            100
                                        )
                                      : 0}
                                    %
                                  </Typography>
                                  <Box sx={{ width: "100%", position: "relative" }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={
                                        company.creditLimit > 0
                                          ? Math.min(
                                              ((company.creditUsage || 0) /
                                                company.creditLimit) *
                                                100,
                                              100
                                            )
                                          : 0
                                      }
                                      sx={{
                                        height: 8,
                                        borderRadius: 1,
                                        backgroundColor: theme.palette.action.hover,
                                        "& .MuiLinearProgress-bar": {
                                          backgroundColor:
                                            (company.creditUsage || 0) /
                                              company.creditLimit >
                                            0.9
                                              ? theme.palette.error.main
                                              : (company.creditUsage || 0) /
                                                  company.creditLimit >
                                                0.7
                                              ? theme.palette.warning.main
                                              : theme.palette.primary.main,
                                          borderRadius: 1,
                                        },
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Tooltip>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                --
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMenuOpen(e, company);
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </TableContainer>
      </DragDropContext>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => menuCompany && handleOpenCompany(menuCompany.id)}
        >
          Open
        </MenuItem>
        <MenuItem onClick={() => menuCompany && handleEditCompany(menuCompany)}>
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuCompany) {
              navigator.clipboard.writeText(menuCompany.id);
              eventListener({
                type: "SET_SUCCESS",
                message: "Company ID copied to clipboard",
              });
            }
            handleMenuClose();
          }}
        >
          Copy ID
        </MenuItem>
        {menuCompany && menuCompany.uid === currentUser?.uid && (
          <>
            <MenuItem
              onClick={() => menuCompany && handleTransferOwnership(menuCompany)}
            >
              Transfer Ownership
            </MenuItem>
            <MenuItem
              onClick={() =>
                menuCompany && deleteCompany(menuCompany.id, menuCompany.name)
              }
            >
              Delete
            </MenuItem>
          </>
        )}
        {menuCompany && menuCompany.uid !== currentUser?.uid && (
          <MenuItem
            onClick={() =>
              menuCompany && leaveCompany(menuCompany.id, menuCompany.name)
            }
          >
            Leave Company
          </MenuItem>
        )}
      </Menu>

      <CompanyCreateModal
        open={modalOpen}
        onClose={() => {
          setSelectedCompany(null);
          setModalOpen(false);
          getCompanies(false);
        }}
        onSuccess={(company) => {
          onNewOrganization(company);
          getCompanies(false);
          getUsage(); // Refresh usage after creating company
        }}
        selectedCompany={selectedCompany}
        chatApi={chatApi}
        eventListener={eventListener}
        currentUser={currentUser}
        getToken={getToken}
        companyLimits={companyLimits}
        currentPlan={currentPlan}
        availableFeatures={SELECTABLE_FEATURES}
        isOwner={selectedCompany ? selectedCompany.uid === currentUser?.uid : false}
      />

      {selectedCompanyForTransfer && (
        <TransferOwnershipDialog
          open={transferDialogOpen}
          onClose={() => {
            setTransferDialogOpen(false);
            setSelectedCompanyForTransfer(null);
          }}
          company={selectedCompanyForTransfer}
          getToken={getToken}
          eventListener={eventListener}
          currentUser={currentUser}
          onSuccess={() => {
            setTransferDialogOpen(false);
            setSelectedCompanyForTransfer(null);
            getCompanies(false);
          }}
        />
      )}
    </Box>
  );
};

export default Companies;
